import * as React from 'react';
import GroupLabel from './GroupLabel';
import ElasticsearchAPIConnector from "@elastic/search-ui-elasticsearch-connector";
import { SearchProvider, useSearch } from '@elastic/react-search-ui';
import HitsWidget from "./HitsWidget";
import Filter from "./filter/Filter";
import { useOpenSearchBootstrap } from "../../api";
import Preloader from "../Preloader";
import { chartStyles } from "../../helpers";
import ChartLayout from "../ChartLayout";

function providerConfig(options) {
  const config = {
    alwaysSearchOnInitialLoad: true,
    initialState: {},
    searchQuery: {
      sort: [],
      result_fields: {},
      facets: {},
      disjunctiveFacets: []
    },
  };

  config.initialState.resultsPerPage = options?.displayOptions?.itemsPerPage || 250;

  if (options?.sorting && Array.isArray(options.sorting) && options.sorting.length > 0) {
    const firstOption = options.sorting[0];
    config.initialState.sortField = firstOption.field;
    config.initialState.sortDirection = firstOption.direction;
  }

  if (Array.isArray(options?.filters)) {
    for (const facetConfig of options.filters) {
      const { attribute } = facetConfig;
      config.searchQuery.facets[attribute] = { type: 'value', size: 500, sort: 'value' };
      config.searchQuery.disjunctiveFacets.push(attribute);
    }
  }

  return config;
}

export function connectorConfig(host, indexes) {
  const config = {
    host,
    index: indexes,
    connectionOptions: {
      headers: {
        'X-CSRF-Token': window.getCsrfToken(),
      },
    },
  };

  const previewToken = document.getElementById('library-preview-token')?.dataset?.value;
  if (previewToken) {
    config.connectionOptions.headers['x-preview-token'] = previewToken;
  }

  return config;
}

// Merge hit-only filter clauses into requestBody.post_filter without clobbering
// anything the connector already put there.
function appendPostFilterClauses(requestBody, clauses) {
  if (clauses.length === 0) return;
  const existing = requestBody.post_filter;

  if (existing?.bool?.must) {
    requestBody.post_filter = { bool: { must: [...existing.bool.must, ...clauses] } };
  } else if (existing) {
    requestBody.post_filter = { bool: { must: [existing, ...clauses] } };
  } else {
    requestBody.post_filter = { bool: { must: clauses } };
  }
}

function ClearFiltersButton({ filters }) {
  const { clearFilters } = useSearch();

  const preserved = React.useMemo(
    () => (filters || []).filter(f => f.kind !== 'checkboxes').map(f => f.attribute),
    [filters]
  );

  return (
    <div className='d-none faceted-search-clear-filters'>
      <button type='button' onClick={() => clearFilters(preserved)} />
    </div>
  );
}

export default function OpenSearchProvider({defaultIndex, currentGroup, style, lineStyle, title, groups, groupLabel, handleGroupChange}) {
  const host = `${window.location.protocol}//${window.location.host}`;

  const baseFilters = React.useMemo(() => {
    try {
      return currentGroup.filterBy ? JSON.parse(currentGroup.filterBy) : [];
    } catch {
      return [];
    }
  }, [currentGroup.filterBy]);

  const { loading, initialFilters, allValues } = useOpenSearchBootstrap({
    filters: currentGroup.filters,
    baseFilters,
    host,
    index: defaultIndex,
  });

  const facetFields = React.useMemo(
    () => new Set((currentGroup.filters || []).map(f => f.attribute)),
    [currentGroup.filters]
  );

  const connector = React.useMemo(() => {
    return new ElasticsearchAPIConnector({
      ...connectorConfig(host, defaultIndex),
      interceptSearchRequest: ({ requestBody }, next) => {
        delete requestBody._source;
        if (baseFilters.length > 0) {
          // Facet-field scopes must apply to aggregations (they narrow dropdown
          // options to the whitelist); non-facet scopes must NOT, or they'll
          // shrink unrelated field buckets to the intersection.
          const facetTerms = [];
          const hitOnlyTerms = [];
          for (const f of baseFilters) {
            const clause = { terms: { [f.field]: f.values } };
            (facetFields.has(f.field) ? facetTerms : hitOnlyTerms).push(clause);
          }

          if (facetTerms.length > 0) {
            const query = requestBody.query?.bool
              ? requestBody.query
              : { bool: requestBody.query ? { must: [requestBody.query] } : {} };
            query.bool.filter = [...(query.bool.filter || []), ...facetTerms];
            requestBody.query = query;
          }

          appendPostFilterClauses(requestBody, hitOnlyTerms);
        }
        return next(requestBody);
      },
    });
  }, [defaultIndex, host, baseFilters, facetFields]);

  const css = React.useMemo(() => chartStyles(style), [style]);

  const searchConfig = React.useMemo(() => {
    const base = providerConfig(currentGroup);
    return {
      ...base,
      initialState: { ...base.initialState, filters: initialFilters },
      apiConnector: connector,
      trackUrlState: false,
    };
  }, [currentGroup, initialFilters, connector]);

  if (loading) {
    return <Preloader title='Chart'/>;
  }

  const renderFilter = (filter, filterIndex) => (
    <Filter
      key={filter.attribute}
      config={filter}
      isFirstFacet={filterIndex === 0}
      allValues={allValues}
    />
  );

  return (
    // key= forces SearchProvider to fully reset state when the user switches groups.
    <div className='nv-chart-group nv-chart-group--open-search' key={currentGroup.label}>
      <SearchProvider config={searchConfig}>
        <ChartLayout
          css={css}
          title={title}
          currentGroup={currentGroup}
          groups={groups}
          groupLabel={groupLabel}
          handleGroupChange={handleGroupChange}
          renderFilter={renderFilter}
          renderGroupLabel={() => <GroupLabel currentGroup={currentGroup}/>}
          renderHits={() => <HitsWidget chartConfig={currentGroup} lineStyle={lineStyle}/>}
          clearAllOuter={<ClearFiltersButton filters={currentGroup.filters}/>}
        />
      </SearchProvider>
    </div>
  );
}
